import { useRouteLocale } from "@/hooks/useRouteLocale";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useState, useEffect, useCallback, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "@/hooks/useTranslation";
import { useAuth } from "@/hooks/useAuth";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { UserHeader } from "@/components/customer/UserHeader";
import { Settings, Loader2, Edit2, Save, X, Lock, AlertTriangle, Trash2, CreditCard } from "lucide-react";
import { toast } from "sonner";
import { getUserSettings, updateUserSettings, type UserSettings } from "@/services/api/userSettings";
import { getUserProfile, updateUserProfile, changePassword, deleteAccount, type UserProfile } from "@/services/api/userProfile";
import { getUserCredits, type UserCreditsResponse } from "@/services/api/eventPlans";
import { CreditsDisplay } from "@/components/customer/CreditsDisplay";
import { USER_ROUTES, PUBLIC_ROUTES } from "@/routes";

export default function AccountSettings() {
  const { t, changeLanguage } = useTranslation();
  const locale = useRouteLocale();

  const location = useLocation();
  usePageMeta({
    title: t("settings.metaTitle"),
    description: t("settings.metaTitle"),
    locale: locale,
    path: location.pathname,
    noindex: true,
  });
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const currentLocale = locale || "ro";
  const [settings, setSettings] = useState<UserSettings | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [defaultLanguage, setDefaultLanguage] = useState<'ro' | 'en'>('ro');
  /** Ref for the value just selected in the dropdown (avoids race where Save runs before state commit) */
  const languageToSaveRef = useRef<'ro' | 'en' | null>(null);
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileName, setProfileName] = useState('');
  const [profilePhone, setProfilePhone] = useState('');
  const [credits, setCredits] = useState<UserCreditsResponse | null>(null);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);
  
  // Delete account state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Memoize loadSettings to prevent unnecessary re-renders
  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const [userSettings, userProfile, creditsData] = await Promise.all([
        getUserSettings(),
        getUserProfile(),
        getUserCredits().catch(() => ({ standard: 0, premium: 0, credits: { standard: [], premium: [] } })),
      ]);
      setSettings(userSettings);
      setProfile(userProfile);
      setCredits(creditsData);
      setDefaultLanguage(userSettings.defaultLanguage || 'ro');
      setProfileName(userProfile.user_name || '');
      setProfilePhone(userProfile.user_phone || '');
    } catch (error: unknown) {
      console.error('Failed to load user settings:', error);
      toast.error(t('common.failedToLoadSettings'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  // Memoize handleSaveLanguage to prevent unnecessary re-renders
  const handleSaveLanguage = useCallback(async () => {
    const valueToSave = languageToSaveRef.current ?? defaultLanguage;
    languageToSaveRef.current = null;
    try {
      setSaving(true);
      const updated = await updateUserSettings({ defaultLanguage: valueToSave });
      setSettings(updated);
      setDefaultLanguage(valueToSave);
      toast.success(t('common.settingsUpdated'));
      // If saved language differs from current locale, navigate first so URL updates immediately (BDD can assert), then update i18n
      if (valueToSave !== currentLocale) {
        navigate(USER_ROUTES.settings(valueToSave));
        changeLanguage(valueToSave).catch((err) => {
          console.error('Failed to switch i18n language after save', err);
        });
      }
    } catch (error: unknown) {
      console.error('Failed to update settings:', error);
      toast.error(t('common.failedToUpdateSettings'));
    } finally {
      setSaving(false);
    }
  }, [defaultLanguage, currentLocale, navigate, changeLanguage, t]);

  // Handle profile update
  const handleSaveProfile = useCallback(async () => {
    try {
      setSavingProfile(true);
      const updated = await updateUserProfile({
        user_name: profileName,
        user_phone: profilePhone || null,
      });
      setProfile(updated);
      setIsEditingProfile(false);
      toast.success(t('common.settingsUpdated'));
      // Refresh user data to reflect changes
      updateUser((prev) => (prev ? { ...prev, user_name: updated.user_name, user_phone: updated.user_phone ?? undefined } : prev));
    } catch (error: unknown) {
      console.error('Failed to update profile:', error);
      toast.error(t('common.failedToUpdateSettings'));
    } finally {
      setSavingProfile(false);
    }
  }, [profileName, profilePhone, updateUser, t]);

  // Handle cancel profile edit
  const handleCancelProfileEdit = useCallback(() => {
    if (profile) {
      setProfileName(profile.user_name || '');
      setProfilePhone(profile.user_phone || '');
    }
    setIsEditingProfile(false);
  }, [profile]);

  // Handle password change
  const handleChangePassword = useCallback(async () => {
    if (newPassword !== confirmPassword) {
      toast.error(t('settings.password.mismatch'));
      return;
    }
    if (newPassword.length < 8) {
      toast.error(t('settings.password.tooShort'));
      return;
    }
    try {
      setChangingPassword(true);
      await changePassword({
        current_password: currentPassword,
        new_password: newPassword,
        new_password_confirmation: confirmPassword,
      });
      toast.success(t('settings.password.changed'));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error: unknown) {
      console.error('Failed to change password:', error);
      const errorMessage = error instanceof Error ? error.message : t('settings.password.failed');
      toast.error(errorMessage);
    } finally {
      setChangingPassword(false);
    }
  }, [currentPassword, newPassword, confirmPassword, t]);

  // Handle account deletion
  const handleDeleteAccount = useCallback(async () => {
    try {
      setDeleting(true);
      await deleteAccount({ password: deletePassword });
      toast.success(t('settings.delete.success'));
      // Logout and redirect to home
      localStorage.removeItem('token');
      navigate(PUBLIC_ROUTES.home(currentLocale));
      window.location.reload();
    } catch (error: unknown) {
      console.error('Failed to delete account:', error);
      const errorMessage = error instanceof Error ? error.message : t('settings.delete.failed');
      toast.error(errorMessage);
    } finally {
      setDeleting(false);
      setShowDeleteDialog(false);
      setDeletePassword('');
    }
  }, [deletePassword, currentLocale, navigate, t]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-pink-50/30 via-background to-background dark:from-pink-950/10">
        <UserHeader 
          currentLocale={currentLocale}
          active="events"
        />
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <Loader2 className="h-8 w-8 animate-spin text-pink-600" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-pink-50/30 via-background to-background dark:from-pink-950/10">
      <UserHeader 
        currentLocale={currentLocale}
        active="events"
      />
      <div className="container mx-auto px-4 py-8 max-w-4xl min-h-[calc(100vh-4rem)]">
        <div className="mb-6" data-testid="settings-page-header">
          <h1 className="text-3xl font-bold" data-testid="settings-title">{t('navigation.settings')}</h1>
          <p className="text-muted-foreground mt-2">
            {t('settings.description')}
          </p>
        </div>

        <div className="space-y-6">
          {/* Credits Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-pink-600" />
                <CardTitle>{t('settings.credits.title') || 'Event Plan Credits'}</CardTitle>
              </div>
              <CardDescription>
                {t('settings.credits.description') || 'Your available credits from deleted paid events'}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {credits ? (
                credits.standard > 0 || credits.premium > 0 ? (
                  <CreditsDisplay credits={credits} />
                ) : (
                  <p className="text-sm text-muted-foreground">
                    {t('settings.credits.noCredits') || 'You don\'t have any credits yet. Credits are created when you delete a paid event.'}
                  </p>
                )
              ) : (
                <div className="flex items-center gap-2">
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                  <span className="text-sm text-muted-foreground">{t('common.loading')}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Language Preference Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Settings className="h-5 w-5 text-pink-600" />
                <CardTitle>{t('settings.language.title')}</CardTitle>
              </div>
              <CardDescription>
                {t('settings.language.description')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="defaultLanguage">
                  {t('settings.language.label')}
                </Label>
                <Select
                  value={defaultLanguage}
                  onValueChange={(value: string) => {
                    if (value !== 'ro' && value !== 'en') return;
                    languageToSaveRef.current = value;
                    setDefaultLanguage(value);
                  }}
                >
                  <SelectTrigger id="defaultLanguage" data-testid="language-select-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent data-testid="language-select-content">
                    <SelectItem value="ro" data-testid="language-option-ro">Română</SelectItem>
                    <SelectItem value="en" data-testid="language-option-en">English</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  {t('settings.language.current')}: {defaultLanguage === 'ro' ? 'Română' : 'English'}
                </p>
              </div>
              <Button 
                onClick={handleSaveLanguage} 
                disabled={saving || defaultLanguage === settings?.defaultLanguage}
                className="w-full sm:w-auto"
                data-testid="save-language-button"
              >
                {saving ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common.saving')}
                  </>
                ) : (
                  t('common.save')
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Profile Information Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div className="space-y-1.5">
                  <CardTitle>{t('settings.profile.title')}</CardTitle>
                  <CardDescription>
                    {t('settings.profile.description')}
                  </CardDescription>
                </div>
                {!isEditingProfile && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setIsEditingProfile(true)}
                    data-testid="edit-profile-button"
                  >
                    <Edit2 className="h-4 w-4 mr-2" />
                    {t('common.edit')}
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent className="space-y-4" data-testid="profile-information">
              <div className="space-y-2">
                <Label htmlFor="profile-name" data-testid="profile-name-label">{t('auth.name')}</Label>
                {isEditingProfile ? (
                  <Input
                    id="profile-name"
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    data-testid="profile-name-input"
                  />
                ) : (
                  <div className="px-3 py-2 bg-muted rounded-md" data-testid="profile-name-value">
                    {profile?.user_name || user?.user_name || '-'}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label htmlFor="profile-phone" data-testid="profile-phone-label">{t('auth.phone')}</Label>
                {isEditingProfile ? (
                  <Input
                    id="profile-phone"
                    type="tel"
                    value={profilePhone}
                    onChange={(e) => setProfilePhone(e.target.value)}
                    placeholder={t('auth.phone')}
                    data-testid="profile-phone-input"
                  />
                ) : (
                  <div className="px-3 py-2 bg-muted rounded-md" data-testid="profile-phone-value">
                    {profile?.user_phone || user?.user_phone || '-'}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label data-testid="profile-email-label">{t('auth.email')}</Label>
                <div className="px-3 py-2 bg-muted rounded-md" data-testid="profile-email-value">
                  {profile?.user_email || user?.user_email || '-'}
                </div>
                <p className="text-sm text-muted-foreground">
                  {t('settings.profile.emailNote')}
                </p>
              </div>
              {isEditingProfile && (
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={handleSaveProfile}
                    disabled={savingProfile}
                    data-testid="save-profile-button"
                  >
                    {savingProfile ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        {t('common.saving')}
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        {t('common.save')}
                      </>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleCancelProfileEdit}
                    disabled={savingProfile}
                    data-testid="cancel-profile-button"
                  >
                    <X className="mr-2 h-4 w-4" />
                    {t('common.cancel')}
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Change Password Section */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-2">
                <Lock className="h-5 w-5 text-pink-600" />
                <CardTitle>{t('settings.password.title')}</CardTitle>
              </div>
              <CardDescription>
                {t('settings.password.description')}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="current-password">{t('settings.password.current')}</Label>
                <Input
                  id="current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="current-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password">{t('settings.password.new')}</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="new-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-password">{t('settings.password.confirm')}</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  data-testid="confirm-password-input"
                />
              </div>
              <Button
                onClick={handleChangePassword}
                disabled={changingPassword || !currentPassword || !newPassword || !confirmPassword}
                data-testid="change-password-button"
              >
                {changingPassword ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('common.saving')}
                  </>
                ) : (
                  <>
                    <Lock className="mr-2 h-4 w-4" />
                    {t('settings.password.change')}
                  </>
                )}
              </Button>
            </CardContent>
          </Card>

          {/* Danger Zone - Delete Account */}
          <Card className="border-destructive/50" data-testid="danger-zone-card">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                <CardTitle className="text-destructive">{t('settings.delete.title')}</CardTitle>
              </div>
              <CardDescription>
                {t('settings.delete.description')}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Alert variant="destructive" className="mb-4">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>{t('settings.delete.warning')}</AlertTitle>
                <AlertDescription>
                  {t('settings.delete.warningDescription')}
                </AlertDescription>
              </Alert>
              <Button
                variant="destructive"
                onClick={() => setShowDeleteDialog(true)}
                data-testid="delete-account-button"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('settings.delete.button')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('settings.delete.confirmTitle')}</DialogTitle>
            <DialogDescription>
              {t('settings.delete.confirmDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="delete-password">{t('settings.delete.enterPassword')}</Label>
              <Input
                id="delete-password"
                type="password"
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="••••••••"
                data-testid="delete-password-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteDialog(false);
                setDeletePassword('');
              }}
              disabled={deleting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={deleting || !deletePassword}
              data-testid="confirm-delete-button"
            >
              {deleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.loading')}
                </>
              ) : (
                <>
                  <Trash2 className="mr-2 h-4 w-4" />
                  {t('settings.delete.confirm')}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

